import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { useUiStrings } from "../strings/ui-strings";

export interface StatsCell {
	/** Stable key. */
	key: string;
	/** Small uppercase label ("Organisations"). */
	label: ReactNode;
	/** The number. Formatted with the provider's `formatNumber`; pass a node for anything else. */
	value: number | ReactNode;
	/** After the number, small and grey: "of 40", "2 M · 1 W". */
	of?: ReactNode;
	/** A small line under the number ("matching the filters"). */
	note?: ReactNode;
}

export interface StatsStripProps {
	cells: ReadonlyArray<StatsCell>;
	/** aria-label of the strip. */
	label?: string;
	className?: string;
}

/**
 * The prototype's compact stats strip (`.slst`): one bordered white band,
 * cells split by hairlines, each an uppercase label over a 22px Barlow Condensed number.
 *
 * Cells are flex items (min 10rem) that wrap on narrow screens — no sideways
 * page scroll. The hairlines are each cell's own top/left edge (a 1px
 * box-shadow), clipped by the strip on its outer edge, so they stay correct
 * however the cells wrap.
 */
export function StatsStrip({ cells, label, className }: StatsStripProps) {
	const { formatNumber } = useUiStrings();
	return (
		<dl
			aria-label={label}
			className={cx("flex flex-wrap overflow-hidden rounded-[0.625rem] border border-gms-line bg-base-100", className)}
		>
			{cells.map((c) => (
				<div
					key={c.key}
					className="flex min-w-[10rem] flex-[1_1_10rem] flex-col gap-[0.1875rem] px-[0.875rem] py-[0.625rem] shadow-[-1px_0_0_var(--color-gms-line),0_-1px_0_var(--color-gms-line)]"
				>
					<dt className="text-[0.6875rem] font-semibold tracking-[0.04em] text-gms-ink-3 uppercase">{c.label}</dt>
					<dd className="flex items-baseline gap-[0.375rem] font-display text-[1.375rem] leading-[1.05] font-bold text-gms-navy-ink tabular-nums">
						{typeof c.value === "number" ? formatNumber(c.value) : c.value}
						{c.of !== undefined && <small className="font-sans text-[0.75rem] font-medium text-gms-ink-3">{c.of}</small>}
					</dd>
					{c.note !== undefined && <dd className="text-[0.71875rem] text-gms-ink-3">{c.note}</dd>}
				</div>
			))}
		</dl>
	);
}
